interface SecureFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  message: string;
  formType?: string;
}

const sanitizeInput = (value: string): string => {
  return value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
              .replace(/[<>'"&]/g, (char) => {
                const htmlEntities: { [key: string]: string } = {
                  '<': '&lt;',
                  '>': '&gt;',
                  '"': '&quot;',
                  "'": '&#x27;',
                  '&': '&amp;'
                };
                return htmlEntities[char] || char;
              })
              .trim();
};

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const submitToZoho = async (formData: SecureFormData): Promise<boolean> => {
  console.log('🔍 Starting Zoho submission with data:', formData);
  
  // Validate and sanitize data
  const sanitizedData = {
    firstName: sanitizeInput(formData.firstName),
    lastName: sanitizeInput(formData.lastName),
    email: sanitizeInput(formData.email),
    company: sanitizeInput(formData.company),
    message: sanitizeInput(formData.message),
    formType: formData.formType ? sanitizeInput(formData.formType) : ''
  };

  console.log('🧹 Sanitized data:', sanitizedData);

  if (!validateEmail(sanitizedData.email)) {
    console.error('❌ Email validation failed:', sanitizedData.email);
    throw new Error('Invalid email format');
  }

  // Rate limiting check
  const lastSubmission = sessionStorage.getItem('lastFormSubmission');
  const now = Date.now();
  if (lastSubmission && (now - parseInt(lastSubmission)) < 30000) {
    console.error('⏰ Rate limit hit. Last submission:', lastSubmission, 'Now:', now);
    throw new Error('Please wait before submitting another form');
  }

  const formBody = new FormData();
  formBody.append('xnQsjsdp', '2eaa648798ce23a7a9a9fdfb797621a05074c8311ce9031824d1b4d79807096f');
  formBody.append('zc_gad', '');
  formBody.append('xmIwtLD', '3b367f412ccfde6c347aca9059e7efedf4cd6eef72f9a51d2beb75a4850be285503386a2c820cee984c68e0e4df8b7e1');
  formBody.append('actionType', 'TGVhZHM=');
  formBody.append('returnURL', 'null');
  formBody.append('First Name', sanitizedData.firstName);
  formBody.append('Last Name', sanitizedData.lastName);
  formBody.append('Email', sanitizedData.email);
  formBody.append('Company', sanitizedData.company);
  
  // Add form type to description if provided
  const description = sanitizedData.formType 
    ? `${sanitizedData.message} [Form Type: ${sanitizedData.formType}]`
    : sanitizedData.message;
  
  formBody.append('Description', description);
  
  formBody.append('aG9uZXlwb3Q', ''); // Honeypot for spam protection

  console.log('📋 FormData prepared. Submitting to Zoho...');

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    const response = await fetch('https://crm.zoho.in/crm/WebToLeadForm', {
      method: 'POST',
      body: formBody,
      mode: 'no-cors',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    console.log('✅ Form submitted successfully to Zoho CRM');
    sessionStorage.setItem('lastFormSubmission', now.toString());
    return true;
  } catch (error) {
    console.error('❌ Zoho form submission failed:', error);
    if (error instanceof Error) {
      console.error('Error details:', error.message, error.stack);
    }
    return false;
  }
};

export type { SecureFormData };